/**
 * AgentNode - AI Agent execution node
 * Supports sub-agents: similarity, reference, structure, image, table, direct_qa, custom
 */

import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { AgentNodeData, AgentType } from '../../../../stores/workflow/types';

interface AgentNodeProps {
  data: AgentNodeData;
  selected?: boolean;
}

const agentTypeConfig: Record<AgentType, { color: string; bgGradient: string; icon: React.ReactNode; label: string }> = {
  similarity: {
    color: 'blue',
    bgGradient: 'from-blue-500 to-blue-700',
    label: 'Similarity',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' />
      </svg>
    )
  },
  reference: {
    color: 'purple',
    bgGradient: 'from-purple-500 to-purple-700',
    label: 'Reference',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={2}
          d='M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253'
        />
      </svg>
    )
  },
  structure: {
    color: 'orange',
    bgGradient: 'from-orange-500 to-orange-700',
    label: 'Structure',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={2}
          d='M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7'
        />
      </svg>
    )
  },
  image: {
    color: 'pink',
    bgGradient: 'from-pink-500 to-pink-700',
    label: 'Image',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={2}
          d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
        />
      </svg>
    )
  },
  table: {
    color: 'yellow',
    bgGradient: 'from-yellow-500 to-yellow-700',
    label: 'Table',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={2}
          d='M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'
        />
      </svg>
    )
  },
  direct_qa: {
    color: 'green',
    bgGradient: 'from-green-500 to-green-700',
    label: 'Direct QA',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={2}
          d='M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z'
        />
      </svg>
    )
  },
  custom: {
    color: 'cyan',
    bgGradient: 'from-cyan-500 to-cyan-700',
    label: 'Custom',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={2}
          d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
        />
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
      </svg>
    )
  }
};

export const AgentNode = memo(({ data, selected }: AgentNodeProps) => {
  const config = agentTypeConfig[data.agentType] || agentTypeConfig.custom;
  const hasTools = data.tools && data.tools.length > 0;
  const hasPrompt = data.systemMessage && data.systemMessage.trim().length > 0;

  return (
    <div
      className={`
        min-w-[200px] rounded-xl border-2 bg-white shadow-lg transition-all
        dark:bg-dark-background-secondary
        ${selected ? 'border-primary ring-4 ring-primary/20' : 'border-gray-200 dark:border-border-dark'}
        ${!hasPrompt ? 'border-dashed border-yellow-400' : ''}
      `}
    >
      {/* Input Handle (top) */}
      <Handle type='target' position={Position.Top} className='!h-3 !w-3 !border-2 !border-white !bg-gray-400' />

      {/* Header with gradient */}
      <div className={`flex items-center gap-2 rounded-t-lg bg-gradient-to-r ${config.bgGradient} px-4 py-2`}>
        <div className='text-white'>{config.icon}</div>
        <div className='flex-1 font-semibold text-white'>{data.label}</div>
        {/* Editable indicator */}
        <div className='rounded-full bg-white/20 px-2 py-0.5 text-xs font-medium text-white'>
          <svg className='inline h-3 w-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z' />
          </svg>
        </div>
      </div>

      {/* Body */}
      <div className='px-4 py-3'>
        {/* Agent Type Badge */}
        <div className='mb-2 flex items-center gap-2'>
          <span className='text-xs text-gray-500 dark:text-dark-text-secondary'>Type:</span>
          <span
            className={`rounded-full bg-${config.color}-100 px-2 py-0.5 text-xs font-medium text-${config.color}-700 dark:bg-${config.color}-900/30 dark:text-${config.color}-400`}
          >
            {config.label}
          </span>
        </div>

        {/* System Message Preview */}
        {hasPrompt ? (
          <div className='mb-2'>
            <div className='truncate text-xs text-gray-500 dark:text-dark-text-secondary'>&quot;{data.systemMessage!.substring(0, 50)}...&quot;</div>
          </div>
        ) : (
          <div className='mb-2 rounded-lg bg-yellow-50 p-2 text-xs text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300'>
            Click to configure prompt
          </div>
        )}

        {/* Tools */}
        {hasTools && (
          <div className='mb-2 flex items-center gap-1'>
            <svg className='h-3 w-3 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
              />
            </svg>
            <span className='text-xs text-gray-500 dark:text-dark-text-secondary'>
              {data.tools?.length} tool{data.tools && data.tools.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}

        {/* Prompt editable indicator */}
        <div className='mt-2 flex items-center gap-1 text-xs text-gray-400 dark:text-dark-text-secondary'>
          <svg className='h-3 w-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z' />
          </svg>
          <span>Click to edit prompt</span>
        </div>
      </div>

      {/* Output Handle (bottom) */}
      <Handle type='source' position={Position.Bottom} className={`!h-3 !w-3 !border-2 !border-white bg-gradient-to-r ${config.bgGradient}`} />
    </div>
  );
});

AgentNode.displayName = 'AgentNode';
